import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllInstagramFeedPosts.graphql';
import { Query, QueryallInstagramFeedPostsArgs } from '../../__generated__/globalTypes';

export const useAllInstagramFeedPostsQuery = (options?: QueryHookOptions<Query, QueryallInstagramFeedPostsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallInstagramFeedPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllInstagramFeedPostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallInstagramFeedPostsArgs>
): QueryResult<Query, QueryallInstagramFeedPostsArgs> & {
  getAllInstagramFeedPosts: LazyQueryExecFunction<Query, QueryallInstagramFeedPostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllInstagramFeedPosts, result] = useLazyQuery<Query, QueryallInstagramFeedPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllInstagramFeedPosts, ...result };
};

export const useAllInstagramFeedPostsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllInstagramFeedPosts = useCallback(
    (options?: Omit<QueryOptions<QueryallInstagramFeedPostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallInstagramFeedPostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllInstagramFeedPosts };
};
// Suspense Query Hooks
export const useAllInstagramFeedPostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallInstagramFeedPostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallInstagramFeedPostsArgs>(QUERY, {
    ...options
  });

  return result;
};
